import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import type { ChatReplyFolderResponse, ChatReplyTemplateResponse } from '@/core/generated/api';
import { chatReplyTemplateService } from '@/entities/ChatReplyTemplate/api/chatReplyTemplateService';
import { chatReplyTemplateToRequest, chatReplyTemplateToValues } from '@/entities/ChatReplyTemplate/mappers';
import { chatReplyTemplateSchema } from '@/entities/ChatReplyTemplate/model/schema';
import type { ChatReplyTemplateValues } from '@/entities/ChatReplyTemplate/model/schema';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import classes from './ChatReplyEditors.module.scss';

const { TextField, SelectField, TextareaField } = createRHFFields<ChatReplyTemplateValues>('chat-reply-template');

interface ChatReplyTemplateEditorProps {
    template?: ChatReplyTemplateResponse;
    folderId: string;
    folders: ChatReplyFolderResponse[];
    onSaved: () => void;
    onClose: () => void;
}

/** Редактирует шаблон сообщения без перехода на другую страницу */
export const ChatReplyTemplateEditor = (
    { template, folderId, folders, onSaved, onClose }: ChatReplyTemplateEditorProps
) => {
    const form = useUnsavedForm<ChatReplyTemplateValues>({
        resolver: zodResolver(chatReplyTemplateSchema),
        defaultValues: chatReplyTemplateToValues(template, folderId),
    });

    const canSave = useCanAccessPermission(template?.id
        ? 'admin.chat-replies.edit'
        : 'admin.chat-replies.create');

    const busy = form.formState.isSubmitting;
    const unchanged = Boolean(template?.id) && !form.formState.isDirty;

    /** Закрывает редактор после проверки изменений */
    const close = (): void => {
        if (busy) return;

        confirmUnsavedChanges(form.formState.isDirty, onClose);
    };

    /** Сохраняет шаблон сообщения */
    const submit = async (values: ChatReplyTemplateValues): Promise<void> => {
        if (!canSave || unchanged) return;

        try {
            await chatReplyTemplateService.saveTemplate(chatReplyTemplateToRequest(values, template?.version), template?.id);
            form.reset(values);
            toast.success('Шаблон сохранён');
            onSaved();
        } catch (error: unknown) {
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось сохранить шаблон') });
        }
    };

    return <Modal
        visible
        onHide={close}
        closable={!busy}
        title={template
            ? 'Изменить шаблон'
            : 'Новый шаблон'
        }
        size="lg"
    >
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(submit)}
                className={classes.form}
                noValidate
            >
                <TextField
                    name="name"
                    label="Название шаблона"
                    maxLength={100}
                    isRequired
                    disabled={busy}
                />
                <SelectField
                    name="folderId"
                    label="Папка"
                    isRequired
                    disabled={busy}
                    options={folders.map(folder => ({
                        id: folder.id ?? '',
                        name: folder.parentId
                            ? `${folders.find(parent => parent.id === folder.parentId)?.name ?? ''} / ${folder.name}`
                            : folder.name ?? '',
                    }))}
                />
                <TextareaField
                    name="text"
                    label="Текст сообщения"
                    rows={8}
                    maxLength={4000}
                    isRequired
                    disabled={busy}
                />
                <p>Обычный текст до 4000 символов. В чате он вставляется в поле ввода и отправляется отдельно.</p>
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                }
                <div className={classes.actions}>
                    <ActionButton
                        type="button"
                        label="Отмена"
                        color="secondary"
                        disabled={busy}
                        onClick={close}
                    />
                    <ActionButton
                        type="submit"
                        label="Сохранить"
                        loading={busy}
                        disabled={!canSave || unchanged}
                    />
                </div>
            </form>
        </FormProvider>
    </Modal>;
};
